// The chat panel of a sourcing event (Lane 1322, slice D). Answers render as
// safe Markdown; every change the chat made shows as a card with the server's
// figures. Changes that delete or replace data arrive as a proposal the buyer
// applies here, through the same routes as the buttons.
import React, { useEffect, useRef, useState } from 'react';
import WorkspaceChatPanel from '../../components/workspace-chat/WorkspaceChatPanel';
import useSourcingChat from './useSourcingChat';
import { formatAmount, formatMoney, portalJoinUrl } from './sourcingApi';
import { CONTROL_ROUTES, applyProposal } from './sourcingChatApply';
import { formatDueAt } from '../sourcing-portal/portalView';
import { ComparisonSummary } from './ComparisonPanel';

function ChangeCard({ card }) {
  const t = card.totals_after;
  return (
    <div className="srce-card">
      <div className="srce-card-t">{card.label}</div>
      {t && (
        <div className="srce-mut">
          Now {Number(t.included || 0).toLocaleString()} lines, {formatMoney(t.spend)} spent,
          {' '}{formatMoney(t.projected_cost)} projected
          {t.incomplete ? `, ${Number(t.incomplete).toLocaleString()} need attention` : ''}.
        </div>
      )}
    </div>
  );
}

// A saved comparison (Lane 1331, slice 2C): the saved record's own figures
// and labels, the same ones the Offers tab shows.
function ComparisonCard({ card, onOpenOffers }) {
  const builtAt = card.result && card.result.built_at;
  return (
    <div className="srce-card">
      <div className="srce-card-t">{card.label}</div>
      <ComparisonSummary summary={card.summary || (card.result && card.result.summary)} builtAt={builtAt} compact />
      {onOpenOffers && (
        <button type="button" className="ll-clr srce-card-link" onClick={onOpenOffers}>Open in Offers</button>
      )}
    </div>
  );
}

// A feedback draft made from chat. Nothing is sent; the buyer reviews and
// sends it from the Round tab (slice 2D).
function FeedbackDraftCard({ card, onOpenRound }) {
  const r = card.result || {};
  const n = Number(r.lines || 0);
  return (
    <div className="srce-card">
      <div className="srce-card-t">
        Feedback draft for {r.supplier_name || 'this supplier'}, {n.toLocaleString()} {n === 1 ? 'part' : 'parts'}. Not sent.
      </div>
      {onOpenRound && (
        <button type="button" className="ll-clr srce-card-link" onClick={onOpenRound}>Review and send in the Round tab</button>
      )}
    </div>
  );
}

const TARGET_TEXT = {
  cut: 'wins the parts at these prices', already_wins: 'already wins these parts',
  blocked: 'cannot win these parts on price alone', no_qualifying_cut: 'no cut up to 90% both wins and lowers your cost',
  incomplete: 'the search did not finish (not verified)', recommendation_out_of_date: 'the recommendation changed first',
  invalid_group: 'these parts are not in the calculation', manual_wins: 'would win at your prices',
  manual_does_not_win: 'would not win at your prices',
};

// Rules, scenarios, calculations and targets made from chat (Lane 1344, slice 3D).
// A calculation or target still running when the answer ended polls here until the
// calculation service has saved it; the figures are the result's own.
function AnalysisCard({ card, api, eventId, currency, refreshKey, onOpenRules, onOpenScenario }) {
  const [job, setJob] = useState(null);
  const [res, setRes] = useState(null);
  const pending = card.state === 'pending' && card.job_id && !(job && ['done', 'failed'].includes(job.status));
  const [gone, setGone] = useState(false);
  useEffect(() => {
    if (!pending || gone) return undefined;
    let stop = false;
    let timer = null;
    const later = (fn, ms) => { timer = setTimeout(fn, ms); };
    const tick = () => api.calculation(eventId, card.job_id).then((j) => {
      if (stop) return;
      setJob(j);
      if (!['done', 'failed'].includes(j.status)) later(tick, 3000);
    }).catch((e) => {
      if (stop) return;
      // Not found or not allowed (another company, the feature switched off): stop.
      if (e && [403, 404].includes(e.status)) setGone(true); else later(tick, 5000);
    });
    later(tick, 1500);
    return () => { stop = true; clearTimeout(timer); };
  }, [pending, gone, api, eventId, card.job_id]);
  const resultId = card.result_id || (job && job.result_id);
  // Amounts come from the result as it is now, and show only while it is current
  // (the card itself carries none: Codex 3D round 1).
  // A target shows only while its recommendation is current (Codex 3D round 2).
  const target = card.target || (job && job.target);
  const readId = card.kind === 'calculation' ? resultId : (target && target.recommendation_id);
  const [seen, setSeen] = useState(0);
  useEffect(() => {
    // Read again when the event changes and every minute while shown, so a card
    // never keeps amounts of a result that went out of date.
    const t = setInterval(() => setSeen((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);
  const readSeq = useRef(0);
  useEffect(() => {
    if (!readId) return;
    // Only the latest read may set the result: an older reply that arrives late never
    // brings back amounts of a result that went out of date (Codex 3D round 3).
    const seq = ++readSeq.current;
    setRes(null);
    api.scenarioResult(eventId, readId, { page: 1 })
      .then((r) => { if (seq === readSeq.current) setRes(r); }).catch(() => {});
  }, [api, eventId, readId, refreshKey, seen]);
  const money = (v) => formatAmount(v, currency);
  const failed = card.state === 'failed' || (job && job.status === 'failed');
  if (card.kind === 'rule') {
    return (
      <div className="srce-card">
        <div className="srce-card-t">{card.label}</div>
        {onOpenRules && <button type="button" className="ll-clr srce-card-link" onClick={onOpenRules}>Open the evaluation rules</button>}
      </div>
    );
  }
  if (card.kind === 'target') {
    const t = target;
    const stale = t && res && !res.current;
    const show = t && res && res.current;
    return (
      <div className="srce-card">
        <div className="srce-card-t">{show ? `${t.supplier_name}: ${TARGET_TEXT[t.outcome] || t.outcome}${t.cut_percent ? ` (a cut of ${t.cut_percent}%)` : ''}.`
          : stale ? 'The recommendation changed since this target. Ask again for a fresh one.'
            : t ? 'Checking the target…' : failed ? 'The target could not be worked out.' : 'Working out a target price…'}</div>
        {show && t.prices && <div className="srce-sub">{Object.entries(t.prices).slice(0, 6).map(([p, v]) => `${p} at ${v}`).join('; ')}.</div>}
        <div className="srce-sub">A what-if for you. Nothing is sent to the supplier.</div>
      </div>
    );
  }
  const totals = res && res.current ? res.totals : null;
  const status = (res && res.status) || card.status;
  const label = card.kind === 'scenario' ? card.label
    : failed ? `Scenario ${card.number || ''} could not be calculated`
      : resultId ? `Calculated scenario ${card.number || ''}` : `Calculating scenario ${card.number || ''}…`;
  return (
    <div className="srce-card">
      <div className="srce-card-t">{label}{card.name ? `: ${card.name}` : ''}</div>
      {totals && ['optimal', 'feasible'].includes(status) && (
        <div className="srce-mut">Projected {money(totals.projected)}; quoted {money(totals.quoted)}
          {totals.kept_as_today && Number(totals.kept_as_today) ? `, kept as today ${money(totals.kept_as_today)}` : ''}.
          {status === 'feasible' ? ' Not proven to be the best answer.' : ''}</div>
      )}
      {res && !res.current && <div className="srce-mut">Out of date: the event changed since. Open it to calculate again.</div>}
      {gone && <div className="srce-mut">This calculation is no longer available here.</div>}
      {status === 'infeasible' && <div className="srce-mut">The rules cannot all hold. Open it to see why.</div>}
      {onOpenScenario && card.scenario_id && (
        <button type="button" className="ll-clr srce-card-link" onClick={() => onOpenScenario(card.scenario_id)}>Open in Scenarios</button>
      )}
    </div>
  );
}

function EventCard({ card, onOpenOffers, onOpenRound, ...rest }) {
  if (card.type === 'analysis') return <AnalysisCard card={card} {...rest} />;
  if (card.type === 'comparison') return <ComparisonCard card={card} onOpenOffers={onOpenOffers} />;
  if (card.type === 'feedback_draft') return <FeedbackDraftCard card={card} onOpenRound={onOpenRound} />;
  return <ChangeCard card={card} />;
}

// Lane 1388 (Ryan): the examples name only this event's own suppliers and categories
// (the server sends them); without them, these, which name no supplier.
const GENERIC_EXAMPLES = ['exclude the lines with no purchases', "what if today's suppliers keep everything?"];

// The key was used already: this proposal was applied before (another click,
// another tab, or before a reload).
const alreadyApplied = (e) => e && e.code === 'idempotency_key_reused';

// A scenario or rule the chat proposed (UI redesign plan 4.9). The server wrote every
// word of it from the saved definition. "Yes" here, or yes in the next message, makes
// exactly this; a newer proposal replaces it.
// A scenario card, easy to read (Ryan, slice 2b): the name as its title, the question
// under it, then "What will run" as a short list of the server's own sentences.
function scenarioCardParts(details) {
  let name = null;
  let question = null;
  const runs = [];
  (details || []).forEach((d) => {
    if (d.startsWith('Name: ')) name = d.slice(6);
    else if (d.startsWith('Question: ')) question = d.slice(10);
    else runs.push(d);
  });
  return { name, question, runs };
}

function ConfirmCard({ card, status, busy, error, onYes }) {
  const done = status === 'consumed';
  const replaced = status === 'replaced';
  const parts = card.kind === 'rule' ? null : scenarioCardParts(card.details);
  return (
    <div className={`srce-card ${done || replaced ? 'srce-card--quiet' : 'srce-card--proposal'}`}>
      <div className="srce-card-t">{card.label}{parts && parts.name ? `: ${parts.name}` : ''}</div>
      {parts ? (
        <>
          {parts.question && <div className="srce-card-q">{parts.question}</div>}
          <div className="srce-card-h">What will run</div>
          <ul className="srce-card-list">{parts.runs.map((d, i) => <li key={i}>{d}</li>)}</ul>
        </>
      ) : (
        <ul className="srce-card-list">{(card.details || []).map((d, i) => <li key={i}>{d}</li>)}</ul>
      )}
      {done && <div className="srce-good">Done</div>}
      {replaced && <div className="srce-mut">Replaced by a newer proposal, or out of date.</div>}
      {!done && !replaced && (
        <div className="srce-row">
          <button type="button" className="srce-btn srce-btn--primary" disabled={busy} onClick={onYes}>
            {busy ? 'Working…' : card.kind === 'rule' ? 'Yes, add it' : 'Yes, make it'}</button>
          <span className="srce-sub">or say yes in your next message</span>
        </div>
      )}
      {error && <div className="srce-crit">{error}</div>}
    </div>
  );
}

// Invitation links, shown once when email is off (the same links the Invitations tab
// shows after publish); with email on the server sends them and returns none.
function CardLinks({ links }) {
  if (!links || !links.length) return null;
  return (
    <div className="srce-card-body">
      Email is off here. Send these links yourself; they are shown only once.
      {links.map((l) => <div key={l.invitation_id}>{l.email}: {portalJoinUrl(l.token)}</div>)}
    </div>
  );
}

// Lane 1375 (Ryan, R4): a card with choices (adding suppliers) has one button per
// choice instead of Apply; the button names what it adds.
function ChoiceButtons({ card, state, chosen, onApply, onDismiss }) {
  const busy = state === 'applying' || state === 'dismissing';
  if (state === 'applied') return <div className="srce-good">{chosen ? `Done: ${chosen}` : 'Applied'}</div>;
  return (
    <div className="srce-row srce-choices">
      {card.choices.map((c) => (
        <button key={c.label} type="button" className="srce-btn srce-btn--primary" disabled={busy}
          onClick={() => onApply(c)}>{c.label}</button>
      ))}
      <button type="button" className="ll-clr" disabled={busy} onClick={onDismiss}>{state === 'dismissing' ? 'Dismissing…' : 'Not now'}</button>
    </div>
  );
}

function ProposalCard({ card, state, error, links, chosen, onApply, onDismiss, onOpenScenario }) {
  if (card.action === 'mark_preferred') {
    // Only the buyer marks a scenario preferred, on its page (the grouped review).
    return (
      <div className="srce-card srce-card--proposal">
        <div className="srce-card-t">{card.label}</div>
        {(card.details || []).map((d, i) => <div key={i} className="srce-sub">{d}</div>)}
        {onOpenScenario && card.input && (
          <button type="button" className="srce-btn srce-btn--primary" onClick={() => onOpenScenario(card.input.scenario_id)}>Open the scenario</button>
        )}
      </div>
    );
  }
  if (state === 'dismissed') {
    return <div className="srce-card srce-card--quiet"><span className="srce-mut">Dismissed: {card.label}</span></div>;
  }
  if (state === 'replaced') {
    return <div className="srce-card srce-card--quiet"><span className="srce-mut">Replaced by a newer change: {card.label}</span></div>;
  }
  return (
    <div className="srce-card srce-card--proposal">
      <div className="srce-card-t">{card.label}</div>
      {(card.details || []).length > 0 && !(state === 'applied' && chosen) && (
        <ul className="srce-card-list">{card.details.map((d, i) => <li key={i}>{d}</li>)}</ul>
      )}
      {card.action === 'set_terms' && card.input && card.input.terms
        && card.input.terms.supplier_description && (
        <div className="srce-card-body">{card.input.terms.supplier_description}</div>
      )}
      {card.action === 'set_goal' && card.input && card.input.goal && (
        <div className="srce-card-body">{card.input.goal}</div>
      )}
      {(card.times || []).filter((t) => t.at).map((t) => (
        <div key={t.label} className="srce-sub">{t.label}: {formatDueAt(t.at)}</div>
      ))}
      {state === 'applied' && <CardLinks links={links} />}
      {Array.isArray(card.choices) && card.choices.length > 0 ? (
        <ChoiceButtons card={card} state={state} chosen={chosen} onApply={onApply} onDismiss={onDismiss} />
      ) : state === 'applied' ? <div className="srce-good">Applied</div> : (
        <div className="srce-row">
          <button type="button" className="srce-btn srce-btn--primary" disabled={state === 'applying' || state === 'dismissing'}
            onClick={onApply}>{state === 'applying' ? 'Applying…' : 'Apply'}</button>
          <button type="button" className="ll-clr" disabled={state === 'applying' || state === 'dismissing'} onClick={onDismiss}>{state === 'dismissing' ? 'Dismissing…' : 'Dismiss'}</button>
        </div>
      )}
      {error && <div className="srce-crit">{error}</div>}
    </div>
  );
}

// `headerActions`: controls for the panel's header ("Feedback"; Lane 1382).
export default function SourcingChatPanel({ api, eventId, revision, currency, refreshKey, onChanged, onClose, onOpenOffers,
  onOpenRound, onOpenRules, onOpenScenario, ask, onFocus, headerActions }) {
  const chat = useSourcingChat({ api, eventId, onChanged, onFocus });
  const [confirming, setConfirming] = useState({});   // proposal_id -> {busy, error}

  const yes = async (card) => {
    setConfirming((prev) => ({ ...prev, [card.proposal_id]: { busy: true } }));
    try {
      const r = await api.confirmProposal(eventId, card.proposal_id, card.conversation_id || chat.conversationId);
      chat.setProposalState(card.proposal_id, { status: 'consumed' });
      setConfirming((prev) => ({ ...prev, [card.proposal_id]: {} }));
      onChanged();
      const made = (r && r.result) || {};
      if (onFocus) {
        onFocus(made.kind === 'rule' ? { tab: 'rules', rule_id: made.rule_id }
          : { tab: 'scenarios', scenario_id: made.scenario_id });
      }
    } catch (e) {
      if (e.code === 'proposal_replaced' || e.code === 'proposal_stale') {
        chat.setProposalState(card.proposal_id, { status: 'replaced' });
      }
      setConfirming((prev) => ({ ...prev, [card.proposal_id]: { error: e.message } }));
    }
  };
  const [proposals, setProposals] = useState({});   // proposal_id -> {state, error}
  const revisionRef = useRef(revision);
  revisionRef.current = revision;

  const setProposal = (id, value) => setProposals((prev) => ({ ...prev, [id]: value }));
  // What this screen did wins; otherwise the server's state on load (Lane 1368: a reload
  // shows Applied, Dismissed or Replaced, never Apply again).
  const cardState = (card) => (proposals[card.proposal_id] || {}).state
    || (chat.cardStates || {})[card.proposal_id] || 'open';
  // Dismissed only once the server saved it; otherwise the card stays as it was, with
  // the reason (Lane 1368: a reload must never undo what the screen showed).
  const dismiss = async (card) => {
    setProposal(card.proposal_id, { state: 'dismissing' });
    try {
      await chat.dismissCard(card.proposal_id);
      setProposal(card.proposal_id, { state: 'dismissed' });
    } catch (e) {
      setProposal(card.proposal_id, { state: 'open', error: `Could not dismiss it: ${e.message}` });
    }
  };
  // Per message: the known steps between revisions (from -> to). A step is a
  // change this message saved at once (its change card: revision - 1 ->
  // revision; every change moves the revision by exactly one) or an apply of
  // one of this message's own cards. A card may be applied against a newer
  // revision only when known steps lead there from the revision it was
  // prepared on; any other change in between makes it stale, and the server
  // refuses it, so an old card never overwrites newer work.
  const steps = useRef({});

  const stepsFor = (turn) => {
    const own = steps.current[turn.client_turn_id]
      || (steps.current[turn.client_turn_id] = new Map());
    const all = new Map(own);
    (turn.event_cards || []).forEach((c) => {
      if (c.type === 'change' && typeof c.revision === 'number') all.set(c.revision - 1, c.revision);
    });
    // The server's verified steps for this message (Lane 1368: they survive a reload).
    ((chat.cardSteps || {})[turn.client_turn_id] || []).forEach(([from, to]) => all.set(from, to));
    return { own, all };
  };

  const reaches = (all, from, to) => {
    let r = from;
    const seen = new Set();
    while (r !== to && all.has(r) && !seen.has(r)) { seen.add(r); r = all.get(r); }
    return r === to;
  };

  // ``choice``: the button the buyer clicked on a card with choices (Lane 1375).
  const applyCards = async (turn, cards, choice) => {
    let rev = revisionRef.current;
    for (const card of cards) {
      setProposal(card.proposal_id, { state: 'applying' });
      const { own, all } = stepsFor(turn);
      const expected = reaches(all, card.revision_seen, rev) ? rev : card.revision_seen;
      try {
        const r = await applyProposal(api, eventId, card, expected, choice);
        if (r && typeof r.revision === 'number') {
          own.set(expected, r.revision);
          rev = r.revision;
        }
        // Only S7 cards keep the links (the older invite card is unchanged).
        setProposal(card.proposal_id, CONTROL_ROUTES[card.action] && r && r.links && r.links.length
          ? { state: 'applied', links: r.links }
          : { state: 'applied', ...(choice ? { chosen: choice.label } : {}) });
      } catch (e) {
        if (alreadyApplied(e)) {
          setProposal(card.proposal_id, { state: 'applied' });
        } else if (e.code === 'stale_revision' && chat.controlOn) {
          // The event moved on since this card: it is replaced, as a reload would say.
          setProposal(card.proposal_id, { state: 'replaced' });
          break;
        } else {
          setProposal(card.proposal_id, {
            state: 'open',
            error: e.code === 'stale_revision'
              ? 'The event changed since this was prepared. Ask again for a fresh change.'
              : e.message,
          });
          break;
        }
      }
    }
    onChanged();
  };

  // This event's cards under each message (the shared panel draws the rest).
  const renderCards = (t) => (
    <>
      {(t.event_cards || []).map((card, i) => (card.type === 'proposal'
        ? <ConfirmCard key={card.proposal_id} card={card}
          status={(chat.proposals[card.proposal_id] || {}).status || card.state}
          busy={Boolean((confirming[card.proposal_id] || {}).busy)}
          error={(confirming[card.proposal_id] || {}).error} onYes={() => yes(card)} />
        : card.type === 'proposed_change'
        ? <ProposalCard key={card.proposal_id || i} card={card}
          state={cardState(card)}
          error={(proposals[card.proposal_id] || {}).error}
          links={(proposals[card.proposal_id] || {}).links}
          chosen={(proposals[card.proposal_id] || {}).chosen || (chat.cardChoices || {})[card.proposal_id]}
          onApply={(choice) => applyCards(t, [card], choice)} onOpenScenario={onOpenScenario}
          onDismiss={() => dismiss(card)} />
        : <EventCard key={`${card.type}-${card.revision}-${i}`} card={card} onOpenOffers={onOpenOffers} onOpenRound={onOpenRound}
          api={api} eventId={eventId} currency={currency || 'USD'} refreshKey={refreshKey}
          onOpenRules={onOpenRules} onOpenScenario={onOpenScenario} />))}
      {(() => {
        // A card with choices waits for the buyer's pick: never part of "Apply all".
        const open = (t.event_cards || []).filter((c) => c.type === 'proposed_change' && c.action !== 'mark_preferred'
          && !(Array.isArray(c.choices) && c.choices.length > 0)
          && !['applied', 'dismissed', 'applying', 'dismissing', 'replaced'].includes(cardState(c)));
        return open.length > 1 && !t.pending ? (
          <button type="button" className="srce-btn srce-btn--primary srce-applyall"
            onClick={() => applyCards(t, open)}>
            Apply all {open.length} changes</button>
        ) : null;
      })()}
    </>
  );

  return (
    <WorkspaceChatPanel
      chat={chat}
      title="Chat with Wingman"
      ariaLabel="Chat about this event"
      unavailableText="Chat is not available here. You can still make every change with the buttons."
      emptyState={(
        <>
          Ask about these parts, or ask for a change, for example
          {' '}{(chat.examples || GENERIC_EXAMPLES).map((x, i) => (
            <React.Fragment key={i}>{i > 0 ? ' or ' : ''}&ldquo;{x}&rdquo;</React.Fragment>
          ))}
          {' '}A new scenario or rule is shown first as a card: say yes to make it.
        </>
      )}
      placeholder="Ask about this event…"
      topNote={chat.intakeNote ? (
        <div className="srce-intake-note" data-testid="srce-intake-note">
          <div>{chat.intakeNote.text}</div>
          {chat.intakeNote.still_open_text && <div className="srce-intake-sub">{chat.intakeNote.still_open_text}</div>}
        </div>
      ) : null}
      renderCards={renderCards}
      onClose={onClose}
      ask={ask}
      headerActions={headerActions}
    />
  );
}
